import './instrument';
// -- imports above this line have side effects

import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  HttpLink,
  ServerError,
} from '@apollo/client';
import { InMemoryCache } from '@apollo/client/cache';
import { ErrorLink } from '@apollo/client/link/error';
import { ApolloProvider } from '@apollo/client/react';
import { GlobalToastRegion } from '@pledge-earth/product-language';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import _store from 'store';

import './styles/index.css';
// don't import components before this line otherwise style precedence will break
import { GoogleMapsAPIProvider } from './components/Map/GoogleMapsAPIProvider';
import { Localization } from './localization';
import { Router, router } from './router';
import { store } from './store/store';
import { showErrorToast } from './utils/toast';

// Sentry
const searchParams = new URLSearchParams(
  window.location.hash.replace(/#\/?/g, ''),
);
if (searchParams.has('access_token')) {
  const accessToken = searchParams.get('access_token');
  const redirectTo = _store.get('redirectTo');
  _store.set('accessToken', accessToken);

  window.location.href = `${window.location.protocol}//${window.location.host}${redirectTo}`;
}

const errorLink = new ErrorLink(({ error }) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message, locations, path }) =>
      // eslint-disable-next-line no-console
      console.error(
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions, @typescript-eslint/no-base-to-string -- eslint onboarding, eslint v9 upgrade
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      ),
    );
  }

  if (ServerError.is(error)) {
    /* eslint-disable-next-line no-console -- eslint onboarding */
    console.error(`[Server error]: ${error}`);

    if (error.statusCode === 401) {
      _store.remove('accessToken');
      showErrorToast({
        description: 'You have been disconnected',
      });
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- eslint onboarding
      router.navigate('/auth/login');
    }
  }
});

const httpLink = new HttpLink({
  uri: process.env.REACT_APP_GRAPHQL_ENDPOINT,
  headers: {
    'apollo-require-preflight': 'true',
    authorization: _store.get('accessToken')
      ? `Bearer ${_store.get('accessToken')}`
      : '',
  },
});

const client = new ApolloClient({
  link: ApolloLink.from([errorLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'ignore',
    },
    query: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  },
});

const container = document.getElementById('root');
const root = createRoot(container!, {
  onUncaughtError: Sentry.reactErrorHandler(),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
});
root.render(
  <ApolloProvider client={client}>
    <Provider store={store}>
      <Localization>
        <GoogleMapsAPIProvider
          apiKey={process.env.REACT_APP_GOOGLE_MAPS_JAVASCRIPT_API_KEY || ''}
        >
          <Router />
          <GlobalToastRegion />
        </GoogleMapsAPIProvider>
      </Localization>
    </Provider>
  </ApolloProvider>,
);
